import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Loader2, X, ArrowRight, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * RuangAdminPanel — Phase A CRUD for `db.ruang` (super admin only).
 *
 * Features: create, edit (whitelisted fields), toggle activate/deactivate (with
 * discussion-count guard), bulk reorder via number inputs. No hard delete.
 * When a deactivate is blocked, exposes a "Lihat N Diskusi" CTA that flips the
 * outer admin dashboard to Community → Threads with the ruang filter pre-set —
 * handoff via `onOpenThreadsWithFilter(ruang_id)` prop.
 */
const GROUPS = [
  { key: "sekolah", label: "Grup A · Sekolah (jenjang)" },
  { key: "Pendidikan dan Kehidupan Anak", label: "Grup B · Pendidikan & Kehidupan Anak" },
];

export default function RuangAdminPanel({ onOpenThreadsWithFilter }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // ruang_id
  const [orderDrafts, setOrderDrafts] = useState({}); // ruang_id -> new sort_order
  const [reorderBusy, setReorderBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // full row when editing
  const [blocked, setBlocked] = useState(null); // {ruang_id, name, discussion_count}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/ruang");
      setRows(r.data);
      setOrderDrafts({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat Ruang");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) (g[r.group] = g[r.group] || []).push(r);
    return g;
  }, [rows]);

  const dirty = Object.keys(orderDrafts).length > 0;

  const toggleActive = async (r) => {
    if (pending) return;
    setPending(r.ruang_id);
    try {
      const resp = await api.post(`/admin/ruang/${r.ruang_id}/toggle-active`, { is_active: !r.is_active });
      if (resp.data.blocked) {
        setBlocked({ ruang_id: r.ruang_id, name: r.name, discussion_count: resp.data.discussion_count });
        return;
      }
      toast.success(r.is_active ? `${r.name} dinonaktifkan` : `${r.name} diaktifkan`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah status");
    } finally { setPending(null); }
  };

  const saveOrder = async () => {
    setReorderBusy(true);
    try {
      const orders = Object.entries(orderDrafts).map(([ruang_id, sort_order]) => ({ ruang_id, sort_order: Number(sort_order) }));
      const r = await api.post("/admin/ruang/reorder", { orders });
      toast.success(`Urutan disimpan (${r.data.modified} baris)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan urutan");
    } finally { setReorderBusy(false); }
  };

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (r) => { setEditing(r); setFormOpen(true); };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-[#4B5563]"><Loader2 className="w-6 h-6 animate-spin" /></div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-ruang-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-serif-display text-xl text-brand-primary">Kelola Ruang</h3>
          <p className="text-xs text-[#4B5563] mt-1">Konfigurasi struktur Ruang forum. Super admin only. Tidak ada hard delete — gunakan toggle Aktif untuk menyembunyikan Ruang.</p>
        </div>
        <div className="flex gap-2">
          {dirty && (
            <Button onClick={saveOrder} disabled={reorderBusy} data-testid="ruang-save-order" className="rounded-full bg-[#1E3A8A] hover:bg-[#14294D] text-white h-9">
              {reorderBusy ? "Menyimpan…" : "Simpan urutan"}
            </Button>
          )}
          <Button onClick={openCreate} data-testid="ruang-create-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#C15C3F] text-white h-9">
            <Plus size={14} className="mr-1" /> Ruang baru
          </Button>
        </div>
      </div>

      {blocked && (
        <div className="rounded-2xl bg-[#FFFBEB] border border-[#FCD34D] p-4 flex items-start gap-3" data-testid="ruang-deactivate-blocked">
          <AlertTriangle size={18} className="text-[#B45309] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#92400E]">Tidak dapat menonaktifkan "{blocked.name}"</div>
            <div className="text-sm text-[#78350F] mt-1">Ruang ini masih memiliki <strong>{blocked.discussion_count} diskusi</strong>. Pindahkan diskusi terlebih dahulu.</div>
            <div className="mt-3 flex gap-2">
              <Button
                onClick={() => { onOpenThreadsWithFilter && onOpenThreadsWithFilter(blocked.ruang_id); setBlocked(null); }}
                data-testid="ruang-deactivate-blocked-cta"
                className="rounded-full bg-[#1E3A8A] hover:bg-[#14294D] text-white h-8"
              >
                Lihat {blocked.discussion_count} Diskusi <ArrowRight size={12} className="ml-1" />
              </Button>
              <Button variant="outline" onClick={() => setBlocked(null)} className="rounded-full h-8">Tutup</Button>
            </div>
          </div>
        </div>
      )}

      {GROUPS.map((g) => (
        <div key={g.key} className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
          <div className="px-4 py-2.5 bg-[#F3F1ED] text-xs uppercase tracking-widest text-[#4B5563]">{g.label} · {(grouped[g.key] || []).length}</div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-[#4B5563]">
              <tr>
                <th className="text-left p-3 w-20">Urutan</th>
                <th className="text-left p-3">Ruang</th>
                <th className="text-left p-3 w-40">Slug</th>
                <th className="text-right p-3 w-24">Member</th>
                <th className="text-right p-3 w-24">Diskusi</th>
                <th className="text-center p-3 w-24">Aktif</th>
                <th className="text-right p-3 w-24">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {(grouped[g.key] || []).map((r) => (
                <tr key={r.ruang_id} className="border-t border-[#EAE7E0]" data-testid={`ruang-row-${r.ruang_id}`}>
                  <td className="p-3">
                    <input
                      type="number"
                      value={orderDrafts[r.ruang_id] ?? r.sort_order}
                      onChange={(e) => setOrderDrafts((prev) => ({ ...prev, [r.ruang_id]: e.target.value }))}
                      data-testid={`ruang-sort-${r.ruang_id}`}
                      className="w-16 h-8 rounded-lg border border-[#EAE7E0] px-2 text-sm text-center"
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg" aria-hidden>{r.icon}</span>
                      <div>
                        <div className="font-semibold text-[#1F2937]">{r.name}</div>
                        {r.description && <div className="text-xs text-[#4B5563] line-clamp-1">{r.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs text-[#6B7280] font-mono">{r.slug}</td>
                  <td className="p-3 text-right text-[#4B5563]">{r.member_count}</td>
                  <td className="p-3 text-right text-[#4B5563]">{r.discussion_count}</td>
                  <td className="p-3 text-center">
                    <button
                      onClick={() => toggleActive(r)}
                      disabled={pending === r.ruang_id}
                      data-testid={`ruang-toggle-active-${r.ruang_id}`}
                      className={`inline-flex items-center h-7 px-3 rounded-full text-xs font-semibold transition-colors ${
                        r.is_active
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                          : "bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200"
                      } disabled:opacity-50`}
                    >
                      {r.is_active ? "Aktif" : "Non-aktif"}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <Button variant="outline" onClick={() => openEdit(r)} data-testid={`ruang-edit-${r.ruang_id}`} className="rounded-full h-8 border-[#EAE7E0]">
                      <Pencil size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
              {(grouped[g.key] || []).length === 0 && (
                <tr><td colSpan={7} className="p-4 text-center text-xs text-[#4B5563]">Belum ada Ruang di grup ini.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {formOpen && (
        <RuangForm
          initial={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function RuangForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    icon: initial?.icon || "",
    group: initial?.group || "sekolah",
    description: initial?.description || "",
    intro_prompt: initial?.intro_prompt || "",
    cover_image: initial?.cover_image || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.icon.trim()) {
      toast.error("Nama dan icon wajib diisi");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        icon: form.icon.trim(),
        group: form.group,
        description: form.description.trim(),
        intro_prompt: form.intro_prompt.trim(),
        cover_image: form.cover_image.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/admin/ruang/${initial.ruang_id}`, payload);
        toast.success("Ruang diperbarui");
      } else {
        await api.post("/admin/ruang", payload);
        toast.success("Ruang dibuat");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" data-testid="ruang-form-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EAE7E0]">
          <h4 className="font-serif-display text-lg text-brand-primary">{isEdit ? "Edit Ruang" : "Ruang baru"}</h4>
          <button onClick={onClose} aria-label="Tutup" className="w-8 h-8 rounded-full hover:bg-[#F3F1ED] flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {isEdit && (
            <div className="text-xs text-[#6B7280]">
              <div>ruang_id: <code className="font-mono">{initial.ruang_id}</code> <span className="italic">(tidak dapat diubah)</span></div>
              <div>slug: <code className="font-mono">{initial.slug}</code> <span className="italic">(tidak dapat diubah)</span></div>
            </div>
          )}
          <Field label="Nama Ruang" required>
            <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ruang-form-name" className="w-full h-10 rounded-xl border border-[#EAE7E0] px-3 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Icon (emoji)" required>
              <input type="text" value={form.icon} onChange={(e) => set("icon", e.target.value)} data-testid="ruang-form-icon" maxLength={8} className="w-full h-10 rounded-xl border border-[#EAE7E0] px-3 text-lg text-center" />
            </Field>
            <Field label="Grup">
              <select value={form.group} onChange={(e) => set("group", e.target.value)} data-testid="ruang-form-group" className="w-full h-10 rounded-xl border border-[#EAE7E0] px-2 text-sm bg-white">
                {GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Deskripsi singkat">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="ruang-form-description" rows={2} maxLength={500} className="w-full rounded-xl border border-[#EAE7E0] p-2 text-sm resize-none" />
          </Field>
          <Field label="Intro prompt (muncul di halaman Ruang)">
            <textarea value={form.intro_prompt} onChange={(e) => set("intro_prompt", e.target.value)} data-testid="ruang-form-intro" rows={2} maxLength={500} className="w-full rounded-xl border border-[#EAE7E0] p-2 text-sm resize-none" />
          </Field>
          <Field label="Cover image URL (opsional)">
            <input type="url" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} data-testid="ruang-form-cover" placeholder="https://…" className="w-full h-10 rounded-xl border border-[#EAE7E0] px-3 text-sm" />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#EAE7E0]">
          <Button variant="outline" onClick={onClose} className="rounded-full h-9">Batal</Button>
          <Button onClick={submit} disabled={busy} data-testid="ruang-form-submit" className="rounded-full bg-[#E07A5F] hover:bg-[#C15C3F] text-white h-9">
            {busy ? "Menyimpan…" : (isEdit ? "Simpan perubahan" : "Buat Ruang")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1 block">{label}{required && <span className="text-[#B91C1C] ml-1">*</span>}</span>
      {children}
    </label>
  );
}
